import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Download, RefreshCw, Settings, Shield, Save, Upload, Trash2, AlertTriangle, Database, CreditCard, Plus, X } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const API = '/api';

export default function SettingsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [dbStats, setDbStats] = useState(null);
  const [backupPoints, setBackupPoints] = useState([]);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [hideData, setHideData] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [newAccName, setNewAccName] = useState('');
  const [newAccNumber, setNewAccNumber] = useState('');
  const [newAccMethod, setNewAccMethod] = useState('instapay');

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [savingPw, setSavingPw] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, statsRes, bpRes] = await Promise.all([
        axios.get(`${API}/settings`, { headers }),
        axios.get(`${API}/data/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/data/backup-points`, { headers }).catch(() => ({ data: [] })),
      ]);
      const s = settingsRes.data || {};
      setMaintenanceMode(s.maintenance_mode === true);
      setHideData(s.hide_partner_data === true);
      try {
        setPaymentAccounts(s.payment_accounts ? JSON.parse(s.payment_accounts) : []);
      } catch { setPaymentAccounts([]); }
      setDbStats(statsRes.data);
      setBackupPoints(bpRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveSetting = async (key, value) => {
    try {
      await axios.post(`${API}/settings`, { key, value: String(value) }, { headers });
    } catch {
      toast.error('فشل حفظ الإعداد');
    }
  };

  const toggleMaintenance = async (checked) => {
    setMaintenanceMode(checked);
    await saveSetting('maintenance_mode', checked);
    toast.success(checked ? 'تم تفعيل وضع الصيانة' : 'تم إيقاف وضع الصيانة');
  };

  const toggleHideData = async (checked) => {
    setHideData(checked);
    await saveSetting('hide_partner_data', checked);
    toast.success(checked ? 'تم إخفاء بيانات الشركاء' : 'تم إظهار بيانات الشركاء');
  };

  const addPaymentAccount = async () => {
    if (!newAccName.trim() || !newAccNumber.trim()) return;
    const updated = [...paymentAccounts, { name: newAccName.trim(), number: newAccNumber.trim(), method: newAccMethod }];
    setPaymentAccounts(updated);
    await saveSetting('payment_accounts', JSON.stringify(updated));
    setNewAccName('');
    setNewAccNumber('');
    setNewAccMethod('instapay');
    toast.success('تم إضافة رقم الدفع');
  };

  const removePaymentAccount = async (index) => {
    const updated = paymentAccounts.filter((_, i) => i !== index);
    setPaymentAccounts(updated);
    await saveSetting('payment_accounts', JSON.stringify(updated));
    toast.success('تم حذف رقم الدفع');
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('كلمة المرور غير متطابقة'); return; }
    if (newPassword.length < 6) { toast.error('6 أحرف على الأقل'); return; }
    setSavingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers });
      toast.success('تم تغيير كلمة المرور');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تغيير كلمة المرور');
    } finally { setSavingPw(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/data/export`, { headers });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `avious_backup_${new Date().toISOString().slice(0,10)}.json`; a.click();
      toast.success('تم تصدير البيانات');
    } catch { toast.error('فشل التصدير'); }
    finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      await axios.post(`${API}/data/import`, { data, mode: 'merge' }, { headers });
      toast.success('تم استيراد البيانات');
      fetchSettings();
    } catch { toast.error('فشل الاستيراد'); }
  };

  const handleBackup = async () => {
    setCreatingBackup(true);
    try {
      await axios.post(`${API}/data/backup-point`, null, { headers });
      toast.success('تم إنشاء نقطة استعادة');
      fetchSettings();
    } catch { toast.error('فشل إنشاء النقطة'); }
    finally { setCreatingBackup(false); }
  };

  const handleRestore = async (id) => {
    if (!window.confirm('سيتم استبدال جميع البيانات!')) return;
    try {
      await axios.post(`${API}/data/restore/${id}`, null, { headers });
      toast.success('تم الاستعادة'); window.location.reload();
    } catch { toast.error('فشل الاستعادة'); }
  };

  const handleFullReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/system/full-reset`, {}, { headers });
      toast.success('تم إعادة التهيئة');
      window.location.reload();
    } catch { toast.error('فشل إعادة التهيئة'); }
    finally { setResetting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" dir="rtl"><RefreshCw className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-6" dir="rtl" data-testid="settings-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-black text-emerald-800 flex items-center gap-3">
              <Settings className="h-8 w-8" />
              الإعدادات
            </h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin')} className="rounded-full">
            <ArrowLeft className="ml-2 h-4 w-4" />
            العودة
          </Button>
        </div>

        <div className="space-y-6">
          {/* System Toggles */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-emerald-700">إعدادات النظام</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div>
                  <p className="font-bold text-amber-800">وضع الصيانة</p>
                  <p className="text-sm text-amber-600">عند التفعيل يظهر تنبيه للشركاء</p>
                </div>
                <Switch checked={maintenanceMode} onCheckedChange={toggleMaintenance} data-testid="maintenance-toggle" />
              </div>
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div>
                  <p className="font-bold text-blue-800">إخفاء بيانات الشركاء</p>
                  <p className="text-sm text-blue-600">إخفاء الأرباح والأرصدة من لوحة الشريك</p>
                </div>
                <Switch checked={hideData} onCheckedChange={toggleHideData} data-testid="hide-data-toggle" />
              </div>
            </CardContent>
          </Card>

          {/* Payment Accounts */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-emerald-700">
                <CreditCard className="h-5 w-5" />
                أرقام الدفع والتحويل
              </CardTitle>
              <CardDescription>هذه الأرقام تظهر للشركاء عند طلب الإيداع</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentAccounts.map((acc, i) => (
                <div key={i} className="flex items-center justify-between bg-emerald-50 p-3 rounded-xl border border-emerald-200">
                  <div>
                    <p className="font-bold text-emerald-800">{acc.name} <span className="text-xs font-normal text-emerald-600 mr-1">({({instapay:'انستاباي',vodafone_cash:'فودافون كاش',bank_transfer:'تحويل بنكي',cash:'نقدي'})[acc.method] || acc.method || 'غير محدد'})</span></p>
                    <p className="font-mono text-emerald-600 select-all">{acc.number}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removePaymentAccount(i)} data-testid={`remove-acc-${i}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2 flex-wrap">
                <select value={newAccMethod} onChange={e => setNewAccMethod(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="new-acc-method">
                  <option value="instapay">انستاباي</option>
                  <option value="vodafone_cash">فودافون كاش</option>
                  <option value="bank_transfer">تحويل بنكي</option>
                  <option value="cash">نقدي</option>
                </select>
                <Input placeholder="الاسم (مثال: حساب فودافون)" value={newAccName} onChange={e => setNewAccName(e.target.value)} className="flex-1 min-w-[140px]" data-testid="new-acc-name" />
                <Input placeholder="الرقم" value={newAccNumber} onChange={e => setNewAccNumber(e.target.value)} className="flex-1 min-w-[140px]" data-testid="new-acc-number" />
                <Button onClick={addPaymentAccount} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="add-acc-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-purple-700">
                <Shield className="h-5 w-5" />
                تغيير كلمة المرور
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input type="password" placeholder="كلمة المرور الحالية" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
              <Input type="password" placeholder="كلمة المرور الجديدة" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <Input type="password" placeholder="تأكيد كلمة المرور الجديدة" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              <Button onClick={handleChangePassword} disabled={savingPw || !currentPassword || !newPassword}
                className="rounded-full bg-purple-600 hover:bg-purple-700" data-testid="change-password-btn">
                {savingPw ? <RefreshCw className="ml-2 h-4 w-4 animate-spin" /> : <Shield className="ml-2 h-4 w-4" />}
                تغيير
              </Button>
            </CardContent>
          </Card>

          {/* Data Protection */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-green-700">
                <Database className="h-5 w-5" />
                حماية البيانات
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {dbStats && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 p-4 rounded-xl text-center border border-green-200">
                    <p className="text-sm text-green-600">الجداول</p>
                    <p className="text-2xl font-black text-green-700">{dbStats.total_collections}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-xl text-center border border-green-200">
                    <p className="text-sm text-green-600">السجلات</p>
                    <p className="text-2xl font-black text-green-700">{dbStats.total_documents}</p>
                  </div>
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <Button onClick={handleExport} disabled={exporting} className="rounded-full" data-testid="export-btn">
                  {exporting ? <RefreshCw className="ml-2 h-4 w-4 animate-spin" /> : <Download className="ml-2 h-4 w-4" />}
                  تصدير JSON
                </Button>
                <Label className="rounded-full cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 border text-sm font-medium">
                  <Upload className="h-4 w-4" />
                  استيراد
                  <Input type="file" accept=".json" onChange={handleImport} className="hidden" data-testid="import-input" />
                </Label>
                <Button onClick={handleBackup} disabled={creatingBackup} variant="outline" className="rounded-full" data-testid="backup-btn">
                  {creatingBackup ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span className="mr-1">نقطة استعادة</span>
                </Button>
              </div>

              {backupPoints.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>التاريخ</TableHead><TableHead>استعادة</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {backupPoints.slice(0, 5).map(bp => (
                        <TableRow key={bp.id}>
                          <TableCell className="text-sm">{new Date(bp.created_at).toLocaleString('ar-EG')}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => handleRestore(bp.id)}>
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Full Reset */}
          <Card className="border-red-200 bg-red-50/30 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                إعادة تهيئة البرنامج
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="rounded-full w-full" disabled={resetting} data-testid="full-reset-btn">
                    {resetting ? <RefreshCw className="ml-2 h-4 w-4 animate-spin" /> : <Trash2 className="ml-2 h-4 w-4" />}
                    إعادة تهيئة كاملة
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent dir="rtl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-red-700">تأكيد الإعادة</AlertDialogTitle>
                    <AlertDialogDescription>
                      سيتم حذف جميع الشركاء والتوزيعات. هذا الإجراء لا يمكن التراجع عنه!
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex gap-2">
                    <AlertDialogCancel>إلغاء</AlertDialogCancel>
                    <AlertDialogAction onClick={handleFullReset} className="bg-red-600 hover:bg-red-700">تأكيد</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
